import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { LIFESTYLE } from "../lib/data";

const CHAPTERS = [
  { n: "01", title: "It started with a walk", body: "Not a business plan. A dog, a leash, and the same loop around the neighborhood every morning — the small ritual that quietly holds a day together." },
  { n: "02", title: "The gear kept getting in the way", body: "Harnesses that rubbed. Leashes that tangled. Collars with tags that jingled and eventually fell off. None of it was made for the way people and dogs actually move." },
  { n: "03", title: "So we made our own", body: "We started with three things we use every single day: a no-pull harness, a leash that gives room to roam, and a collar that always knows the way home." },
  { n: "04", title: "Every mile, together", body: "Kindred Mile isn't about pets as accessories. It's about the distance you cover side by side — the ordinary walks and the unplanned detours. We make gear for that distance." },
];

export default function Story() {
  useEffect(() => window.scrollTo(0, 0), []);
  return (
    <div className="pt-28 md:pt-36" data-testid="story-page">
      <header className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="font-manrope text-xs uppercase tracking-[0.15em] font-bold text-rust">Our story</p>
          <h1 className="mt-4 max-w-4xl font-fraunces text-5xl font-light leading-[1.02] md:text-7xl">
            We make gear for the distance you share.
          </h1>
        </Reveal>
      </header>

      <Reveal className="mx-auto mt-14 max-w-[1400px] px-5 md:px-10">
        <img src={LIFESTYLE.woods} alt="A person and dog in the woods" className="lifestyle-img aspect-[16/9] w-full object-cover" />
      </Reveal>

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28">
        <div className="grid gap-y-16 md:grid-cols-12 md:gap-x-16">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05} className={`md:col-span-6 ${i % 2 ? "md:col-start-7" : ""}`}>
              <span className="font-fraunces text-2xl text-rust">{c.n}</span>
              <h2 className="mt-3 font-fraunces text-3xl leading-tight md:text-4xl">{c.title}</h2>
              <p className="mt-4 font-manrope text-lg text-ink">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-forest text-cream">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start gap-6 px-5 py-20 md:px-10 md:py-28">
          <h2 className="max-w-2xl font-fraunces text-4xl leading-tight md:text-5xl">Start where every mile starts — the walk.</h2>
          <Link to="/shop" data-testid="story-cta" className="group inline-flex items-center gap-3 rounded-full bg-rust px-8 py-4 font-manrope font-semibold text-cream transition-colors hover:bg-cream hover:text-charcoal">
            Explore the gear <ArrowRight className="hover-arrow h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
